import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { OrderSandbox } from 'app/core/sandboxes/order.sandbox';
import { FilterValues } from 'app/core/interfaces/filtervalues.interface';
import { SessionState } from 'app/core/store/session/session.interface';
import { fadeInAnimation } from 'app/animations';
import * as fromRoot from 'app/core/store';
import { ContractRestrictions } from 'app/core/store/contract-details/contract-details.interface';
import * as orderDetailActions from 'app/core/store/order-detail/order-detail.actions';
import * as ordersActions from 'app/core/store/orders/orders.actions';
import { OrdersClearFilterAction } from 'app/core/store/orders/orders.actions';
import { Customer, UserContext } from 'app/core/store/session/user-context/user-context.interface';
import { util } from 'app/core/util/util';
import { FlorderPageContainer } from 'app/modules/florders/florder-page-container/florder-page.container';
import { distinctUntilChanged, map, takeWhile } from 'rxjs/operators';
import { goAction } from 'app/core/store/router/router.actions';
import { Order, Orders } from 'app/core/interfaces/order.interface';

@Component({
  selector: 'app-order-page',
  templateUrl: './order-page.container.html',
  styleUrls: ['../../florders/florder-page-container/florder-page.container.scss'],
  animations: [fadeInAnimation],
})
export class OrderPageContainer extends FlorderPageContainer {
  orders: Orders;
  restrictions: ContractRestrictions;
  userContext: UserContext;
  customers: Customer[] = null;
  isSuperUser: boolean;
  filterValues: FilterValues;

  constructor(public sandbox: OrderSandbox) {
    super();
    this.store
      .select('orders')
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe((orders: Orders) => {
        this.orders = orders;
        this.filter = util.deepCopy(orders.filter); // We will change this object
        this.totalItems = orders.totalItems;
        this.ordersAreLoading = orders.loading;
        this.isDownloading = orders.downloading;
        this.filterValues = orders.filterValues;
      });
    this.store
      .select('session')
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe((session: SessionState) => {
        this.userContext = session.userContext;
      });
    // this.store.select('editOrderDetail').pipe(takeWhile(() => !this.destroyed)).subscribe((editOrderDetail: EditOrderDetail) => {
    //   this.loading = editOrderDetail.mode.loading;
    // });
    this.store
      .pipe(
        takeWhile(() => !this.destroyed),
        map(state => state.customerInfo),
        distinctUntilChanged()
      )
      .subscribe(customerInfo => {
        this.shipTos = (customerInfo.customer && customerInfo.customer.shipTos) || [];
        this.restrictions = customerInfo.restrictions; // used in list: useCcrValidation
      });
    this.store
      .pipe(
        takeWhile(() => !this.destroyed),
        map(state => state.session),
        distinctUntilChanged()
      )
      .subscribe((session: SessionState) => {
        this.isSuperUser = session.userContext.isSuperUser;
        if (this.isSuperUser) {
          this.customers = session.customerPermissions.data.queryOrGetOrder;
        } else {
          this.customers = null; // if not null, customer is part of filter form
        }
      });
  }

  protected reload(isNew: boolean): void {
    this.store.dispatch(new ordersActions.OrdersLoadAction({ filter: this.filter, isNew }));
  }

  createFlorder(): void {
    if (this.authorization.ORDER.CREATE) {
      this.store.dispatch(
        goAction({
          path: ['orders/new'],
          query: { back: 'orders' },
        })
      );
    }
  }

  createTemplate(): void {
    if (this.authorization.ORDER.CREATE_TEMPLATE) {
      this.store.dispatch(
        goAction({
          path: ['/orders/template/new'],
          query: { back: 'orders' },
        })
      );
    }
  }

  openOrder(florder: Order): void {
    if (this.authorization.ORDER.GET) {
      let params;
      if (florder.salesOrderNumber) {
        params = { salesOrderNumber: florder.salesOrderNumber, back: 'orders' };
      } else {
        params = { back: 'orders' };
      }
      this.store.dispatch(
        goAction({
          path: [`orders/${florder.etmOrderNumber}`],
          query: params,
        })
      );
    }
  }

  downloadFile(exportType: string): void {
    if (this.authorization.ORDER.LIST_ORDERS_AS_DOCUMENT && !this.isDownloading) {
      const customerIds = this.isSuperUser ? this.customers.map(c => c.id) : [];
      this.store.dispatch(new ordersActions.OrdersDownloadAction({ exportType, filter: this.filter, totalItems: this.totalItems, customerIds }));
    }
  }

  createCCR(order: Order): void {
    this.store.dispatch(
      goAction({
        path: [`/ccr/${order.etmOrderNumber}`],
        query: { salesOrderNumber: order.salesOrderNumber, createOrEdit: 'create' },
      })
    );
  }

  editCCR(order: Order): void {
    this.store.dispatch(
      goAction({
        path: [`/ccr/${order.etmOrderNumber}/edit/${order.ccr.ccrNumber}`],
        query: { salesOrderNumber: order.salesOrderNumber, createOrEdit: 'edit' },
      })
    );
  }

  copyOrder(order: Order): void {
    this.sandbox.copyOrder(order.etmOrderNumber);
  }

  asTemplate(order: Order): void {
    this.sandbox.createTemplateFromOrder({ etmOrderNumber: order.etmOrderNumber });
  }

  acceptDeviation(ccrNumber: string): void {
    this.store.dispatch(new ordersActions.OrderAcceptDeviationAction({ ccrNumber }));
  }

  rejectDeviation(ccrNumber: string): void {
    this.store.dispatch(new ordersActions.OrderRejectDeviationAction({ ccrNumber }));
  }

  downloadDocument(orderDocumentNumber: string): void {
    this.store.dispatch(new orderDetailActions.OrderDetailDocumentDownloadAction({ documentId: orderDocumentNumber, extension: 'PDF' }));
  }

  downloadCCRDocument(ccrDocumentNumber: string): void {
    this.store.dispatch(new orderDetailActions.OrderDetailCCRDocumentAction({ documentId: ccrDocumentNumber, extension: 'PDF' }));
  }

  resetFilter() {
    this.store.dispatch(new OrdersClearFilterAction());
  }
}
